import React, { useState } from 'react'
import { Collapse, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { HelpContentData, HelpTextData } from '../HelpDrawer'
import { useCollapsibleHelpStyles } from './CollapsibleHelpContent.styles'
import { useStyles } from './HelpContent.styles'

export function isCollapsiblePersonaBlock(
  helpData: HelpContentData,
  answers: HelpTextData[]
): answers is [HelpTextData, ...HelpTextData[]] {
  if (!helpData.isCollapsible || answers.length < 2) {
    return false
  }
  const [first, ...rest] = answers
  if (first?.type !== 'persona') {
    return false
  }
  return rest.every(
    item =>
      item.type === 'faq' &&
      Boolean(item.question?.trim()) &&
      Array.isArray(item.helpAnswer)
  )
}

export type CollapsibleHelpContentProps = {
  header: React.ReactNode
  faqSections: HelpTextData[]
  renderFaqAnswer: (answer: HelpTextData, index: number) => React.ReactNode
}

export const CollapsibleHelpContent = React.memo(
  ({ header, faqSections, renderFaqAnswer }: CollapsibleHelpContentProps) => {
    const { classes: collapsibleClasses } = useCollapsibleHelpStyles()
    const { classes } = useStyles()
    const [expanded, setExpanded] = useState(true)

    return (
      <div className={collapsibleClasses.personaCollapsibleRoot}>
        <button
          type="button"
          className={collapsibleClasses.personaCollapsibleHeader}
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
          data-testid="help-persona-collapsible-toggle"
        >
          {header}
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className={collapsibleClasses.personaChevron}
            aria-hidden
          />
        </button>
        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          <div className={collapsibleClasses.personaCollapsibleBody}>
            {faqSections.map((faq, i) => (
              <div key={i} className={classes.qnaContainer}>
                <div data-testid="question">
                  <Typography className={classes.questionText}>
                    {faq.question}
                  </Typography>
                </div>
                {(faq.helpAnswer ?? []).map((data, j) =>
                  renderFaqAnswer(data, j)
                )}
              </div>
            ))}
          </div>
        </Collapse>
      </div>
    )
  }
)
